/**
 * Timezone utility functions for handling UTC to local time conversion
 */

/**
 * Converts a UTC time string to local time and formats it for display
 * @param utcTimeString - UTC time string from the backend
 * @param options - Intl.DateTimeFormatOptions for formatting
 * @returns Formatted local time string
 */
export function formatUTCTimeToLocal(
  utcTimeString: string, 
  options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  }
): string {
  if (!utcTimeString) return 'N/A';
  
  try {
    // Create a Date object from the UTC string
    const utcDate = new Date(utcTimeString);
    
    // Check if the date is valid
    if (isNaN(utcDate.getTime())) {
      return 'Invalid Date';
    }
    
    // Format to local time
    return utcDate.toLocaleString('en-US', options);
  } catch {
    return 'Invalid Date';
  }
}

/**
 * Formats a UTC time string to show only the date in local time
 * @param utcTimeString - UTC time string from the backend
 * @returns Formatted local date string
 */
export function formatUTCDateToLocal(utcTimeString: string): string {
  return formatUTCTimeToLocal(utcTimeString, {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
}

/**
 * Formats a UTC time string to show only the time in local time
 * @param utcTimeString - UTC time string from the backend
 * @returns Formatted local time string
 */
export function formatUTCTimeOnlyToLocal(utcTimeString: string): string {
  return formatUTCTimeToLocal(utcTimeString, {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  });
}

/**
 * Formats a UTC time string to show date and time in local time
 * @param utcTimeString - UTC time string from the backend
 * @returns Formatted local date and time string
 */
export function formatUTCDateTimeToLocal(utcTimeString: string): string {
  return formatUTCTimeToLocal(utcTimeString, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  });
}

/**
 * Calculates duration between two UTC time strings in local time
 * @param startTime - UTC start time string
 * @param endTime - UTC end time string (optional, defaults to now)
 * @returns Duration in hours and minutes
 */
export function calculateDuration(startTime: string, endTime?: string): string {
  if (!startTime) return 'N/A';
  
  try {
    const start = new Date(startTime);
    const end = endTime ? new Date(endTime) : new Date();
    
    if (isNaN(start.getTime()) || isNaN(end.getTime())) {
      return 'Invalid Date';
    }
    
    const diffMs = end.getTime() - start.getTime();
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffMinutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
    
    if (diffHours > 0) {
      return `${diffHours}h ${diffMinutes}m`;
    } else {
      return `${diffMinutes}m`;
    }
  } catch {
    return 'Invalid Duration';
  }
}

/**
 * Checks if a UTC time string represents a date in the past
 * @param utcTimeString - UTC time string from the backend
 * @returns true if the time is in the past
 */
export function isPastTime(utcTimeString: string): boolean {
  if (!utcTimeString) return false;
  
  try {
    const utcDate = new Date(utcTimeString);
    return utcDate < new Date();
  } catch {
    return false;
  }
}

/**
 * Gets the current local time as a formatted string
 * @param options - Intl.DateTimeFormatOptions for formatting
 * @returns Current local time string
 */
export function getCurrentLocalTime(options: Intl.DateTimeFormatOptions = {
  year: 'numeric',
  month: 'short',
  day: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hour12: true
}): string {
  return new Date().toLocaleString('en-US', options);
}
